<script lang="ts" setup>
import DatacenterSection from '@panel/components/node/create/datacenter/DatacenterSection.vue';
import FlavourSection from '@panel/components/node/create/flavour/FlavourSection.vue';
import FleetConfigurationSection from '@panel/components/node/create/fleet/FleetConfigurationSection.vue';
import ImageSection from '@panel/components/node/create/image/ImageSection.vue';
import ExternalLink from 'gpcore-uikit/components/Component.ExternalLink.vue';
import LoginMethodSection, {
  type Authentication,
} from '@panel/components/node/create/login-method/LoginMedthodSection.vue';
import SettingsSection from '@panel/components/node/create/settings/SettingsSection.vue';
import OrderSummary from '@panel/components/node/create/OrderSummary.vue';
import visibleSectionsSymbol from '@panel/components/node/create/symbols';
import type { OrderSections } from '@panel/components/node/create/types';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import { BillingPeriod } from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import { type Flavour } from 'gpcore-grpc/gpcore/api/cloud/v1/flavour_pb';
import { type BasicImage } from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import { type Datacenter } from 'gpcore-grpc/gpcore/api/cloud/v1/region_pb';
import { provide, reactive } from 'vue';
import OrderLayout from '@panel/layouts/order/Layout.Order.vue';
import { GpAlert, GpBtnLink } from '@gportal/uikit';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import Layout from '@panel/layouts/Layout.vue';
import useProjectViewTitle from '@panel/composables/useProjectViewTitle';

useProjectViewTitle('Create Node');

type OrderState = {
  datacenter: Datacenter;
  flavour: Flavour;
  image: BasicImage;
  billingPeriod: BillingPeriod;
  authentication: Authentication;
  settings: {
    userData: string;
  };
  nodes: Array<string>;
};
const order = reactive<OrderState>({
  datacenter: null,
  flavour: null,
  image: null,
  billingPeriod: BillingPeriod.MONTHLY,
  authentication: null,
  settings: {
    userData: '',
  },
  nodes: new Array<string>(),
});

const projectStore = useProjectStore();

const onChangeNodes = (nodes: Array<string>) => {
  order.nodes = new Array<string>();
  nodes.forEach((fqdn: string) => {
    if (fqdn && fqdn.length > 0) {
      order.nodes.push(fqdn.trim());
    }
  });
};

const sectionsMapping = reactive<OrderSections>({
  datacenter: { label: 'Datacenter', isMounted: false, isVisible: false },
  flavour: { label: 'Flavour', isMounted: false, isVisible: false },
  image: { label: 'Image', isMounted: false, isVisible: false },
  login: { label: 'Login method', isMounted: false, isVisible: false },
  settings: { label: 'Settings', isMounted: false, isVisible: false },
  nodes: { label: 'Nodes', isMounted: false, isVisible: false },
});
provide(visibleSectionsSymbol, sectionsMapping);
</script>

<template>
  <Layout v-playwright="'nodes-create-view'">
    <OrderLayout>
      <template #main>
        <GpAlert v-if="projectStore.projectNodeLimit === 0" alert-type="warning" class="tw-mb-4">
          Please contact our
          <ExternalLink :url="$config.support.url" class="tw-text-orange-800">
            sales team
          </ExternalLink>
          to onboard your account. Currently, the project owner is not fully activated and can't
          create nodes. Please request a node limit for the project owner.
        </GpAlert>

        <GpAlert alert-type="warning" class="tw-mb-5">
          <p>
            <strong>Notice:</strong> When ordering, the contractually agreed prices apply. If you
            have any questions, please contact your account manager, who will be happy to help.
          </p>
        </GpAlert>

        <h2>Create nodes</h2>

        <section
          v-if="!projectStore.hasBillingProfile"
          class="tw-mt-8 tw-px-4 xl:tw-px-8 2xl:tw-px-0"
        >
          <GpAlert alert-type="danger">
            <h4 class="tw-mb-4">Warning</h4>
            <p class="tw-mb-4">
              No billing profile set. You are not able to create nodes without a billing profile.
            </p>
            <div class="tw-text-end">
              <router-link
                v-slot="{ navigate, href }"
                :to="{
                  name: 'ProjectSettings',
                  params: { projectId: projectStore.projectId },
                  hash: '#billing',
                }"
                custom
              >
                <GpBtnLink
                  :href="href"
                  :icon="faArrowRight"
                  btn-type="danger"
                  class="tw-text-gp-mouse"
                  label="Setup billing"
                  size="sm"
                  @click="navigate"
                />
              </router-link>
            </div>
          </GpAlert>
        </section>
        <DatacenterSection v-model="order.datacenter" />
        <transition name="fade">
          <FlavourSection
            v-if="order.datacenter"
            v-model="order.flavour"
            v-model:billing-period="order.billingPeriod"
            :current-datacenter="order.datacenter"
          />
        </transition>
        <transition name="fade">
          <ImageSection
            v-if="order.flavour"
            v-model="order.image"
            :flavour="order.flavour"
            pre-title="Step 3"
            title="Select an image"
          />
        </transition>
        <transition-group name="fade">
          <template v-if="order.flavour && order.image">
            <LoginMethodSection
              v-model="order.authentication"
              :current-image="order.image"
              pre-title="Step 4"
              title="Select a login method"
            />
            <SettingsSection v-model="order.settings" pre-title="Step 5" title="Settings" />
            <FleetConfigurationSection :nodes="order.nodes" @set-nodes="onChangeNodes" />
          </template>
        </transition-group>
      </template>
      <template #summary>
        <OrderSummary :order="order" :sections-mapping="sectionsMapping" />
      </template>
    </OrderLayout>
  </Layout>
</template>
